// The "Add quote" / "Edit quote" modal of a charter request; its state and logic live in useQuoteForm.
import { Alert, Col, DatePicker, Descriptions, Form, Input, Modal, Row, Select, Space, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

import { AirportSelect } from '../../components/AirportSelect';
import { formatCategory } from '../../data/airportLabel';
// SHAR-917 - numeric fields that refuse letters and symbols as they are typed.
import NumberInput from '../../components/NumberInput';
import { commissionLabel, gbp } from '../../data/format';
import { MONEY_MAX } from '../../data/limits';
import type { QuoteForm } from './useQuoteForm';
import { clockName, fmtInstantAt, wallToInstant } from '../../data/airportTime';

const { Text } = Typography;

/** SHAR-50/51 — active records first, inactive (greyed, not selectable) after. */
function activeFirst<T extends { status?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => Number(a.status === 'inactive') - Number(b.status === 'inactive'));
}

export function QuoteFormModal({ quote }: { quote: QuoteForm }) {
  const {
    form,
    editing,
    modalOpen,
    setModalOpen,
    submit,
    submitGuard,
    createMut,
    updateMut,
    operatorId,
    setOperatorId,
    operatorsQ,
    aircraftQ,
    selectedAircraft,
    setOriginAp,
    setDestAp,
    watchedDeparture,
    watchedDuration,
    originTz,
    destTz,
    durationTouched,
    marginTouched,
    breakdown,
    previewError,
  } = quote;
  // Admin regression 6 Oct — the API's refusal, shown IN the modal: a toast
  // behind an open modal was easy to miss, and a 500 said nothing at all.
  const saveError = (createMut.error ?? updateMut.error) as any;
  const saveMessage: string | null = saveError
    ? saveError?.response?.data?.message || (editing ? 'Update failed' : 'Create failed')
    : null;
  return (
    <Modal
      title={editing ? 'Edit quote' : 'Add quote'}
      open={modalOpen}
      onCancel={() => setModalOpen(false)}
      onOk={submit}
      confirmLoading={submitGuard.busy || createMut.isPending || updateMut.isPending}
      okText={editing ? 'Save' : 'Create'}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {/* SHAR-612 — the required rule reached Tail number but not Operator, so
            only one of the pair carried the asterisk. Both identify the quoted
            aircraft; neither is optional. */}
        <Form.Item name="operatorId" label="Operator" rules={[{ required: true, message: 'Required' }]}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            onChange={(v) => { setOperatorId(v); form.setFieldsValue({ aircraftId: undefined }); }}
            // SHAR-51 — an inactive operator's aircraft are all inactive, so it
            // cannot be quoted; shown greyed rather than hidden, so an existing
            // quote still reads its operator.
            // Active first: the greyed ones sit at the end, out of the way.
            options={activeFirst(operatorsQ.data?.items ?? []).map((o) => ({
              value: o.id,
              label: o.status === 'inactive' ? `${o.name} (inactive)` : o.name,
              disabled: o.status === 'inactive',
            }))}
          />
        </Form.Item>
        {/* SHAR-612 — operator and tail number are what identify the quoted
            aircraft, so both are required rather than quietly optional. */}
        <Form.Item name="aircraftId" label="Tail number / aircraft" rules={[{ required: true, message: 'Required' }]}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!operatorId}
            placeholder={operatorId ? 'Select aircraft' : 'Select an operator first'}
            // SHAR-50 — a deactivated aircraft is listed but cannot be picked for a
            // quote; one already on an existing quote still shows as its value.
            options={activeFirst(aircraftQ.data?.items ?? []).map((a) => ({
              value: a.id,
              label: a.status === 'active' ? `${a.modelName} · ${a.tailNumber}` : `${a.modelName} · ${a.tailNumber} (inactive)`,
              disabled: a.status !== 'active',
            }))}
          />
        </Form.Item>

        {/* SHAR-599 — the aircraft particulars, shown as soon as a tail number
            is picked, on both the add and the edit form. Read-only: they
            belong to the aircraft record, not to this quote. */}
        {/* SHAR-599 (retest, 19 Aug) — ONE column, not two. At two columns inside
            this modal each value cell was about a quarter of the width, which
            is narrower than the values it has to hold: "Cessna Citation
            Latitude" and "Midsize Jet" broke mid-word and QA read the block as
            unfinished. A single column gives every value the full width and
            matches the pricing block further down, which is the layout this
            was meant to look like. The label column is pinned so the five rows
            line up instead of each finding its own width. */}
        {selectedAircraft ? (
          <Descriptions
            size="small"
            bordered
            column={1}
            labelStyle={{ width: 220 }}
            style={{ marginBottom: 16 }}
          >
            {/* SHAR-599 (retest) — the order QA asked for: Model first, then
                Category. Their mock-up is five label/value rows in that
                sequence, which is what this is. */}
            <Descriptions.Item label="Model">{selectedAircraft.modelName}</Descriptions.Item>
            <Descriptions.Item label="Category">{formatCategory(selectedAircraft.category)}</Descriptions.Item>
            <Descriptions.Item label="Seats">{selectedAircraft.seatingCapacity}</Descriptions.Item>
            <Descriptions.Item label="Year of manufacture">{selectedAircraft.yearOfManufacture ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Year of interior refurbishment">
              {selectedAircraft.yearOfRefurbishment ?? '—'}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        {/* SHAR-600 — these were free-text boxes labelled ICAO that in practice
            held IATA. They are now the same airport typeahead the rest of the
            admin uses, which resolves to ICAO and shows city and country. */}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="originCode" label="Origin" rules={[{ required: true, message: 'Required' }]}>
              <AirportSelect onSelectAirport={setOriginAp} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="destCode" label="Destination" rules={[{ required: true, message: 'Required' }]}>
              <AirportSelect onSelectAirport={setDestAp} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="departureDateTime"
              label="Departure (date & time)"
              // SHAR-1192 — typed on the origin airport's clock, as the apps show it
              extra={`Origin airport time: ${clockName(originTz)}`}
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* SHAR-601 — arrival is departure plus the flight duration, so it
                is shown rather than asked for: two fields that must agree are
                two fields that can disagree. */}
            <Form.Item label="Arrival (date & time)" extra={`Destination airport time: ${clockName(destTz)}`}>
              <Input
                readOnly
                value={
                  watchedDeparture && watchedDuration
                    ? // SHAR-1192 — the departure instant on the origin's clock plus the
                      // flight time, read on the destination's clock
                      originTz || destTz
                      ? fmtInstantAt(
                          new Date(new Date(wallToInstant(dayjs(watchedDeparture), originTz)).getTime() + Number(watchedDuration) * 3_600_000),
                          destTz,
                        )
                      : dayjs(watchedDeparture).add(Number(watchedDuration) * 60, 'minute').format('DD/MM/YYYY HH:mm')
                    : ''
                }
                placeholder="Set departure and duration"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* SHAR-603 — prefilled from the route and the aircraft's cruise speed,
            and still editable, because the estimate is a starting point.
            SHAR-615 — entered as HOURS AND MINUTES rather than a decimal number
            of hours. A flight is quoted as "2h 30m", not as 2.5, and asking an
            admin to convert in their head is where a 2h 15m flight gets typed
            as 2.15. The stored value is unchanged - still decimal hours - so
            nothing downstream of this form has to know. */}
        <Form.Item label="Flight duration" tooltip="Estimated from the route and the aircraft's cruise speed. Edit if the operator quotes otherwise.">
          <Space.Compact style={{ width: '100%' }}>
            <NumberInput
              min={0}
              max={99}
              precision={0}
              style={{ width: '50%' }}
              addonAfter="h"
              value={watchedDuration != null ? Math.floor(Number(watchedDuration)) : undefined}
              onChange={(h) => {
                const mins = watchedDuration != null
                  ? Math.round((Number(watchedDuration) % 1) * 60)
                  : 0;
                // SHAR-603 - typed by hand, so the estimate stops following.
                durationTouched.current = true;
                form.setFieldsValue({
                  flightDurationHours: (Number(h ?? 0)) + mins / 60,
                });
              }}
            />
            <NumberInput
              min={0}
              max={59}
              precision={0}
              step={5}
              style={{ width: '50%' }}
              addonAfter="m"
              value={watchedDuration != null ? Math.round((Number(watchedDuration) % 1) * 60) : undefined}
              onChange={(m) => {
                const hrs = watchedDuration != null ? Math.floor(Number(watchedDuration)) : 0;
                // SHAR-603 - typed by hand, so the estimate stops following.
                durationTouched.current = true;
                form.setFieldsValue({
                  flightDurationHours: hrs + Number(m ?? 0) / 60,
                });
              }}
            />
          </Space.Compact>
        </Form.Item>
        {/* The real field, kept in the form so submit/reset/prefill are unchanged. */}
        <Form.Item name="flightDurationHours" hidden><NumberInput /></Form.Item>

        {/* SHAR-604/605 — the operator quotes in their own currency; the total
            the user sees is built from it here. */}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="operatorPriceAmount" label="Operator price" rules={[{ required: true, message: 'Required' }]}>
              {/* Admin regression 6 Oct — capped at the API's MONEY_MAX. */}
              <NumberInput min={0} max={MONEY_MAX} style={{ width: '100%' }} addonAfter={
                <Form.Item name="operatorPriceCcy" noStyle>
                  <Select
                    style={{ width: 90 }}
                    options={['GBP', 'EUR', 'USD', 'CHF', 'AED'].map((c) => ({ value: c, label: c }))}
                  />
                </Form.Item>
              } />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* SHAR-604 - typing here takes ownership of the value: the field
                stops following the house rate from that point on. */}
            <Form.Item name="marginGbp" label="ShareJet margin (GBP)">
              <NumberInput
                min={0}
                max={MONEY_MAX}
                style={{ width: '100%' }}
                onChange={() => { marginTouched.current = true; }}
              />
            </Form.Item>
          </Col>
        </Row>

        {breakdown ? (
          <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Operator price in GBP">{gbp(breakdown.operatorPriceGbp)}</Descriptions.Item>
            <Descriptions.Item label="ShareJet margin">{gbp(breakdown.marginGbp)}</Descriptions.Item>
            <Descriptions.Item label={commissionLabel(breakdown.processingFeePct)}>{gbp(breakdown.processingFeeGbp)}</Descriptions.Item>
            <Descriptions.Item label="Total price shown to user">
              {/* PO, 29 Sep — hold + balance: exactly what the app will show. */}
              <Text strong>{gbp(breakdown.displayTotalGbp ?? breakdown.totalGbp)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Pre-auth already paid">
              {/* PO, 29 Sep — the hold as the user paid it, fee included. */}
              {breakdown.preAuthExpired ? <Tag color="red">Expired</Tag> : `${gbp(breakdown.preAuthChargedGbp ?? breakdown.preAuthPaidGbp)}`}
            </Descriptions.Item>
            <Descriptions.Item label="Remaining to be paid by user">
              <Text strong>{gbp(breakdown.remainingGbp)}</Text>
            </Descriptions.Item>
          </Descriptions>
        ) : null}
        {previewError ? (
          <Alert type="error" showIcon message={previewError} style={{ marginBottom: 16 }} />
        ) : null}

        {/* SHAR-606 — defaults to a week out. SHAR-613 — and cannot be set in
            the past, which the form used to accept without complaint. */}
        <Form.Item
          name="expiryDate"
          label="Quote expiry"
          rules={[
            {
              // SHAR-640 — compares MOMENTS, not days. It used to be
              // `isBefore(dayjs(), 'day')`, which only rejected a date before
              // today: an expiry set to an hour that had already passed TODAY
              // sailed through, was stored as a past instant and came back
              // "Expired" the moment it was sent. The server was right all
              // along - it stores an instant and compares instants - so the
              // only place this could be caught is here, before it is saved.
              validator: (_, value: Dayjs | undefined) =>
                !value || !value.isBefore(dayjs())
                  ? Promise.resolve()
                  : Promise.reject(new Error('Expiry cannot be in the past')),
            },
          ]}
          // SHAR-640 — the picker works in the admin's OWN timezone, which is
          // the second half of what QA hit: 14:00 typed in Kyiv is 11:00 UTC,
          // so a time that reads as future locally can already be past. Saying
          // which zone the number is in removes the guess.
          tooltip={`Times are in your local timezone (${dayjs().format('Z')} UTC)`}
        >
          {/* SHAR-616 — a quote expires at a MOMENT, not on a day. Without a
              time the form could only say "expires some time on the 27th",
              and the code then had to invent one. showTime makes the admin's
              intent explicit.
              SHAR-616 (retest) — every minute, not a 15-minute step. The step
              was copied from the departure picker for consistency, but the two
              are not the same kind of value: a departure slot is negotiated in
              quarter hours, while an expiry is a deadline the admin sets to
              whatever they agreed with the operator. Rounding it to :00/:15/
              :30/:45 silently moved that deadline by up to fifteen minutes. */}
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="DD/MM/YYYY HH:mm"
            style={{ width: '100%' }}
            disabledDate={(d) => !!d && d.isBefore(dayjs(), 'day')}
            // SHAR-640 — and on TODAY, the hours and minutes that have already
            // gone are greyed out too. The validator below refuses a past
            // moment; this stops it being offered in the first place, which is
            // the difference between a form that corrects the admin and one
            // that never lets them make the mistake.
            disabledTime={(d) => {
              const now = dayjs();
              if (!d || !d.isSame(now, 'day')) return {};
              return {
                disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                disabledMinutes: (h: number) =>
                  h === now.hour() ? Array.from({ length: now.minute() + 1 }, (_, i) => i) : [],
              };
            }}
          />
        </Form.Item>
        {saveMessage ? <Alert type="error" showIcon message={saveMessage} /> : null}
      </Form>
    </Modal>
  );
}
